import {useCallback} from 'react';

import {Conversation} from '@stripe-internal/conversations-client';
import {Input} from '@stripe-internal/conversations-ui';

import {AssistantInput} from '../../../core/components/AssistantInput';
import {useConversationEnded} from '../../../StripeAssistant/support/hooks/useConversationEnded';
import {
  useThreadsApp,
  useThreadsConversationViewModel,
  useThreadsConversationViewModelIsEnded,
  useThreadsConversationViewModelState,
  useThreadsCurrentConversationOverview,
  useThreadsVisibility,
  useThreadsWithHistory,
} from '../../app/hooks';

type Props = {
  conversation?: Conversation | null;
};

const noSendTyping = () => {
  // no-op
};

function ThreadsActiveConversationInput({
  conversation,
  pending,
}: {
  conversation: Conversation;
  pending: boolean;
}) {
  const app = useThreadsApp();
  const withHistory = useThreadsWithHistory();
  const {visibility} = useThreadsVisibility();
  const overview = useThreadsCurrentConversationOverview();
  const isViewModelEnded = useThreadsConversationViewModelIsEnded();
  const isLiveConvoEnded = useConversationEnded(conversation);
  const isEnded = isViewModelEnded || isLiveConvoEnded;

  if (isEnded) {
    return withHistory ? null : <ThreadsNewConversationInput />;
  }

  if (
    app.runtime === 'managed' &&
    withHistory &&
    overview?.request_a_call?.status === 'queued'
  ) {
    return null;
  }

  return (
    <AssistantInput
      conversation={conversation}
      pending={pending}
      focusInput={visibility === 'open'}
    />
  );
}

function ThreadsNewConversationInput() {
  const viewModel = useThreadsConversationViewModel();
  const {loading} = useThreadsConversationViewModelState();
  const {visibility} = useThreadsVisibility();

  const onSendMessage = useCallback(
    async (message: string) => {
      await viewModel.createNewConversation({
        initialMessage: message,
      });
    },
    [viewModel],
  );

  return (
    <Input
      pending={loading}
      focusInput={visibility === 'open'}
      onSendMessage={async (message: string) => {
        await onSendMessage(message);
      }}
      onSendTyping={noSendTyping}
    />
  );
}

export function ThreadsConversationInput({conversation}: Props) {
  const {loading} = useThreadsConversationViewModelState();

  if (!conversation) {
    return <ThreadsNewConversationInput />;
  }

  return (
    <ThreadsActiveConversationInput
      conversation={conversation}
      pending={loading}
    />
  );
}
